import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import {
  BUY, DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PORTFOLIO, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE, DIRECTIONS, SELL, UNTRADED_ENERGY_KEY,
} from 'src/util/constants';

import StackedBarChart from './StackedBarChart';
import {
  TRADE, UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from './chartConstants';
import { compareByX } from '../helpers/chart';
import { getUnitProps, showNoDataMsg } from '../helpers/common';

/**
 * Builds the stable chart series data plus tooltip lookup indexes for the trade chart.
 * Independent of `tooltipTimestamp` so hovering a new bar does not rebuild the chart
 * series. Tooltip-relevant values are indexed by numeric timestamp (`.ts`) in Maps so
 * {@link getTradeTooltipData} can do an O(1) lookup instead of re-scanning all trade
 * data on every hover.
 * @param {Array} tradeDataBuy
 * @param {Array} tradeDataSell
 * @param {string} unitKey
 * @param {string} timezone
 * @returns {{data: object, tooltipByTs: Map, allByTs: Map}} - stable chart data and
 *   tooltip indexes keyed by numeric timestamp.
 */
export const buildTradeChartData = (tradeDataBuy, tradeDataSell, unitKey, timezone) => {
  const processedData = { buy: {}, sell: {} };
  const offset = {};
  const allXAxisValues = {};
  const tooltipSource = new Map();

  DIRECTIONS.forEach((direction) => {
    const tradeData = direction === BUY ? tradeDataBuy : tradeDataSell;
    if (tradeData === null || tradeData.length === 0) {
      return;
    }

    tradeData.forEach((series) => {
      const {
        key, label, subLabel, data: seriesData, meter, property,
        counterPartyUser, counterPartyProperty, tradeType,
      } = series;
      if (Object.entries(seriesData).length === 0) {
        return;
      }
      const seriesDataWithoutNaN = Object.entries(seriesData)
        .filter(([_, { [unitKey]: value }]) => !Number.isNaN(value));

      seriesDataWithoutNaN.forEach(([_, d]) => {
        const { interval, [unitKey]: value } = d;
        const { timestamp } = interval;

        allXAxisValues[timestamp] = true;

        if (!(key in processedData[direction])) { processedData[direction][key] = {}; }
        const dataKey = processedData[direction][key];

        if (!(timestamp in dataKey)) { dataKey[timestamp] = { x: timestamp, y: 0 }; }
        const dataKeyTS = dataKey[timestamp];

        dataKeyTS.y += value;

        if (direction === SELL) {
          if (!(timestamp in offset)) {
            offset[timestamp] = {
              x: timestamp, y: 0, y0: 0,
            };
          }
          offset[timestamp].y -= value;
          offset[timestamp].y0 -= value;
        }

        const tsValue = timestamp.ts;
        if (!tooltipSource.has(tsValue)) {
          tooltipSource.set(tsValue, { buy: {}, sell: {} });
        }
        const bucket = tooltipSource.get(tsValue)[direction];
        if (!(key in bucket)) {
          bucket[key] = {
            key,
            label,
            subLabel,
            value: 0,
            meter,
            property,
            counterPartyUser,
            counterPartyProperty,
            tradeType,
          };
        }
        bucket[key].value += value;
      });
    });
  });

  // Fill in missing values
  Object.keys(allXAxisValues).forEach((x) => {
    Object.keys(processedData).forEach((direction) => {
      Object.keys(processedData[direction]).forEach((key) => {
        if (!(x in processedData[direction][key])) {
          processedData[direction][key][x] = { x: DateTime.fromISO(x, { zone: timezone }), y: 0 };
        }
      });
    });
    if (!(x in offset)) {
      offset[x] = { x: DateTime.fromISO(x, { zone: timezone }), y: 0, y0: 0 };
    }
  });

  // Index the (now zero-filled) series values by timestamp so tooltip lookups
  // for the `all` field are O(1).
  const allSource = new Map();
  Object.values(processedData).forEach((dataByKey) => {
    Object.values(dataByKey).forEach((dataByTimestamp) => {
      Object.values(dataByTimestamp).forEach((point) => {
        const tsValue = point.x.ts;
        if (!allSource.has(tsValue)) { allSource.set(tsValue, []); }
        allSource.get(tsValue).push(point);
      });
    });
  });

  return { data: processedData, tooltipByTs: tooltipSource, allByTs: allSource };
};

/**
 * Looks up the tooltip data for a given timestamp from the indexes built by
 * {@link buildTradeChartData}. Performs an O(1) Map lookup rather than
 * re-scanning all trade data.
 * @param {Map} tooltipByTs
 * @param {Map} allByTs
 * @param {import('luxon').DateTime|null} tooltipTimestamp
 * @returns {{buy: Array, sell: Array, all: Array}} - tooltip data for the timestamp.
 */
export const getTradeTooltipData = (tooltipByTs, allByTs, tooltipTimestamp) => {
  const tsValue = tooltipTimestamp?.ts;
  const bucket = tsValue !== undefined ? tooltipByTs.get(tsValue) : undefined;
  const buyBucket = bucket?.buy || {};
  const sellBucket = bucket?.sell || {};

  // dont show untraded energy if the value is 0
  const buy = Object.values(buyBucket)
    .filter((tradeType) => !(tradeType.value === 0
      && tradeType.key.includes(UNTRADED_ENERGY_KEY)));
  const sell = Object.values(sellBucket)
    .filter((tradeType) => !(tradeType.value === 0
      && tradeType.key.includes(UNTRADED_ENERGY_KEY)));
  const all = tsValue !== undefined ? (allByTs.get(tsValue) || []) : [];

  return { buy, sell, all };
};

/**
 * Builds a memoized tooltip-data getter for a fixed `tooltipByTs`/`allByTs` pair.
 *
 * `mouseFollowTooltips` re-renders the tooltip flyout on every mousemove tick, not
 * just on Voronoi datum transitions, so the returned function can be invoked at
 * mousemove frequency while the pointer sits over a single bar. It caches the last
 * `{ts, result}` pair so repeated calls for the same (still-hovered) timestamp
 * return the same result reference instead of reallocating buy/sell/all arrays,
 * letting memoized consumers (e.g. `TradeTooltipGroups`) skip recomputation.
 * @param {Map} tooltipByTs
 * @param {Map} allByTs
 * @returns {(tooltipTimestamp: import('luxon').DateTime|null) =>
 *   {buy: Array, sell: Array, all: Array}} - memoized tooltip-data getter.
 */
export const createCachedTradeTooltipDataGetter = (tooltipByTs, allByTs) => {
  let cache = null;
  return (tooltipTimestamp) => {
    const ts = tooltipTimestamp?.ts;
    if (cache && cache.ts === ts) {
      return cache.result;
    }
    const result = getTradeTooltipData(tooltipByTs, allByTs, tooltipTimestamp);
    cache = { ts, result };
    return result;
  };
};

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - ChartTrade component
 */
function ChartTrade({
  aggregation, chartView, handleChartClick, hoverKeys = [], multipleMeter = false,
  selectedKeys = [], timespan, timezone, tooltipUpdateFunc,
  tooltipLabelFunc, tradeDataBuy = [], tradeDataSell = [], unit, width,
}) {
  const dataNotAvailable = (!tradeDataBuy || tradeDataBuy.length === 0)
    && (!tradeDataSell || tradeDataSell.length === 0);

  // Logic to frame the chart data : Start
  const {
    key: unitKey, label: unitLabel, format: yAxisFormat, tooltipFormat,
  } = getUnitProps(unit);
  const tooltipDateFormat = aggregation.match(/^PT\d+[HMS]/) ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT;

  // Stable data preparation: independent of tooltipTimestamp so hovering a new
  // bar does not rebuild the chart series.
  const { data, tooltipByTs, allByTs } = useMemo(
    () => (dataNotAvailable
      ? { data: { buy: {}, sell: {} }, tooltipByTs: new Map(), allByTs: new Map() }
      : buildTradeChartData(tradeDataBuy, tradeDataSell, unitKey, timezone)),
    [dataNotAvailable, tradeDataBuy, tradeDataSell, unitKey, timezone],
  );

  // Recreated only when the underlying indexes change, so the cache inside it
  // persists across tooltip-only re-renders (see `createCachedTradeTooltipDataGetter`).
  const getTooltipDataFunc = useMemo(
    () => createCachedTradeTooltipDataGetter(tooltipByTs, allByTs),
    [tooltipByTs, allByTs],
  );

  const toolTipProps = useMemo(() => ({
    getTooltipDataFunc,
    isCarbon: unit === UNIT_CARBON,
    multipleMeter,
    tooltipDateFormat,
    tooltipFormat,
    tooltipLabelFunc,
    unit,
    unitLabel,
  }), [getTooltipDataFunc, multipleMeter, tooltipDateFormat, tooltipFormat, tooltipLabelFunc,
    unit, unitLabel]);

  const stackBarProps = useMemo(() => ({
    data,
    hoverKeys,
    selectedKeys,
    compareByXFunc: compareByX,
    tooltipUpdateFunc,
    handleChartClick,
    chartType: TRADE,
  }), [data, handleChartClick, hoverKeys, selectedKeys, tooltipUpdateFunc]);

  if (dataNotAvailable) {
    return showNoDataMsg(TRADE);
  }

  return (
    <StackedBarChart
      chartView={chartView}
      stackBarProps={stackBarProps}
      stepSize={aggregation}
      timespan={timespan}
      timezone={timezone}
      toolTipProps={toolTipProps}
      width={width}
      yAxisFormat={yAxisFormat}
    />
  );
}

const tradeDataShape = PropTypes.shape({
  aggregation: PropTypes.string,
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tradeType: PropTypes.string,
  counterPartyUser: PropTypes.shape({ uuid: PropTypes.string }),
  counterPartyProperty: PropTypes.shape({ uuid: PropTypes.string, title: PropTypes.string }),
  data: PropTypes.objectOf(PropTypes.shape({
    interval: PropTypes.shape({
      timestamp: PropTypes.instanceOf(DateTime).isRequired,
    }).isRequired,
    volume: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
  })),
});

ChartTrade.propTypes = {
  aggregation: PropTypes.string.isRequired,
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
  }).isRequired,
  handleChartClick: PropTypes.func.isRequired,
  hoverKeys: PropTypes.arrayOf(PropTypes.string),
  multipleMeter: PropTypes.bool,
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  timezone: PropTypes.string.isRequired,
  tradeDataBuy: PropTypes.arrayOf(tradeDataShape),
  tradeDataSell: PropTypes.arrayOf(tradeDataShape),
  tooltipLabelFunc: PropTypes.func.isRequired,
  tooltipUpdateFunc: PropTypes.func.isRequired,
  unit: PropTypes.oneOf([
    UNIT_CURRENCY,
    UNIT_ENERGY,
    UNIT_CARBON,
  ]).isRequired,
  width: PropTypes.number.isRequired,
};

export default React.memo(ChartTrade);
